import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { motion } from "framer-motion";
import axiosInstance from "../../../interceptors";
import { useTheme } from "../../../context/theme";
import {
  toggleJob,
  setScope,
  selectScopedJobIds,
} from "../../../redux/slices/job-scope-slice";
import {
  fetchPublishedJobsCached,
  fetchAllJobsCached,
  selectPublishedJobs,
  selectAllJobs,
} from "../../../redux/slices/jobsCacheSlice";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, AreaChart, Area, CartesianGrid } from "recharts";
import CreateJobWizard from "../../../components/create-job-wizard/CreateJobWizard";
import SetupChecklist from "../../../components/onboarding/SetupChecklist";
import SampleDataBanner from "../../../components/onboarding/SampleDataBanner";

/* ── Animation variants ─────────────────────────────────────── */
const pageStagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0 } },
};

const fadeUp = {
  hidden: { opacity: 1, y: 0 },
  visible: { opacity: 1, y: 0 },
};

const slideLeft = {
  hidden: { opacity: 1, x: 0 },
  visible: { opacity: 1, x: 0 },
};

const floatLoop = {
  animate: {
    y: [0, -6, 0],
    transition: { duration: 3.5, repeat: Infinity, ease: "easeInOut" },
  },
};

/* ── Icons ───────────────────────────────────────────────────── */
const resumeIcon = (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
    <polyline points="14 2 14 8 20 8" />
  </svg>
);
const jobIcon = (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <rect x="2" y="7" width="20" height="14" rx="2" />
    <path d="M16 7V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v2" />
  </svg>
);
const checkIcon = (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M22 11.08V12a10 10 0 11-5.93-9.14" />
    <polyline points="22 4 12 14.01 9 11.01" />
  </svg>
);
const clockIcon = (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
);

/* ── Sub-components ──────────────────────────────────────────── */

const StatCard = ({ icon, label, value, onClick, trend, iconBg }) => (
  <motion.button
    variants={fadeUp}
    whileHover={{ y: -2, transition: { type: "spring", stiffness: 400, damping: 15 } }}
    whileTap={{ scale: 0.98 }}
    onClick={onClick}
    style={{ background:"white", border:"1px solid #E2E8F0", borderRadius:12, padding:"14px 16px", textAlign:"left", cursor:"pointer", display:"block", width:"100%" }}
  >
    <div style={{ width:28, height:28, borderRadius:8, display:"flex", alignItems:"center", justifyContent:"center", marginBottom:8, background:iconBg||"#EEF2FF", color:"#7B5AC4" }}>
      {icon}
    </div>
    <div style={{ fontSize:24, fontWeight:600, color:"#0F172A", lineHeight:1.1 }}>
      {value}
    </div>
    <div style={{ fontSize:9, color:"#64748B", textTransform:"uppercase", letterSpacing:0.5, marginTop:2 }}>
      {label}
    </div>
    {trend && <span style={{ fontSize:9, padding:"1px 6px", borderRadius:100, fontWeight:500, marginTop:4, display:"inline-block", background:trend>0?"#ECFDF5":"#FEF2F2", color:trend>0?"#059669":"#DC2626" }}>{trend>0?"+":""}{trend} this week</span>}
  </motion.button>
);

const StatusPill = ({ status }) => {
  const map = {
    CREATED: { label: "Awaiting", cls: "badge-neutral" },
    IN_PROGRESS: { label: "Live", cls: "badge-warning" },
    RECORDING_UPLOADED: { label: "Scoring", cls: "badge-warning" },
    SCORED: { label: "Done", cls: "badge-success" },
    FAILED: { label: "Failed", cls: "badge-danger" },
  };
  const v = map[status] || { label: status, cls: "badge-neutral" };
  return <span className={v.cls}>{v.label}</span>;
};

const ScoreBadge = ({ value }) => {
  const v = Math.round(value);
  const cls = v >= 75 ? "badge-success" : v >= 50 ? "badge-warning" : "badge-danger";
  return <span className={`${cls} font-heading font-bold`}>{v}</span>;
};

/* ── Main Component ──────────────────────────────────────────── */

/* ── Analytics sub-components ───────────────────────────────── */

const FunnelBar = ({ label, value, max, color }) => {
  const pct = max > 0 ? Math.round((value / max) * 100) : 0;
  return (
    <div className="flex items-center gap-3 text-sm">
      <span className="w-24 text-right text-vf-muted font-body text-xs shrink-0">{label}</span>
      <div className="flex-1 h-7 rounded bg-vf-tint overflow-hidden">
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${pct}%` }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className={`h-full rounded ${color} flex items-center justify-end pr-2`}
        >
          <span className="text-xs font-semibold text-white font-heading">{value}</span>
        </motion.div>
      </div>
      <span className="text-xs text-vf-muted w-10 text-right font-heading">{pct}%</span>
    </div>
  );
};

const SkeletonAnalytics = () => (
  <div className="flex flex-col gap-4">
    {[...Array(3)].map((_, i) => (
      <div key={i} className="flex items-center gap-3">
        <div className="skeleton h-7 w-24 rounded" />
        <div className="skeleton h-7 flex-1 rounded" />
      </div>
    ))}
  </div>
);

const WeeklyTrend = ({ weeks }) => {
  if (!weeks || weeks.length === 0) return null;
  const max = Math.max(...weeks.map((w) => w.count), 1);
  const symbols = ["▁", "▂", "▃", "▄", "▅", "▆", "▇", "█"];
  return (
    <div className="flex items-end gap-1 font-mono text-lg text-vf-primary">
      {weeks.map((w, i) => {
        const idx = Math.round((w.count / max) * (symbols.length - 1));
        return (
          <span key={i} title={`${w.label}: ${w.count}`}>
            {symbols[idx]}
          </span>
        );
      })}
    </div>
  );
};

const DashboardHome = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const scopedIds = useSelector(selectScopedJobIds);
  const { isDark } = useTheme();
  const [interviews, setInterviews] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(true);
  const [aiPrompts, setAiPrompts] = useState([
    "Find candidates with strong communication scores",
    "Create a new job opening",
    "Add interview questions to a job",
    "Show candidates waiting on interview",
  ]);
  const [emailStats, setEmailStats] = useState(null);
  const [emailEvents, setEmailEvents] = useState([]);
  const [wizardOpen, setWizardOpen] = useState(false);
  const cachedPublishedJobs = useSelector(selectPublishedJobs);
  const cachedAllJobs = useSelector(selectAllJobs);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axiosInstance.post("/job-posting/api/suggestions/list", { surface: "DASHBOARD" });
        const items = data?.data?.items;
        if (Array.isArray(items) && items.length > 0) {
          setAiPrompts(items.map((i) => i.promptText));
        }
      } catch (e) { /* keep fallback */ }
    })();

    // Pulls summary counts + recent events from the commonservice email_log
    // table. Replaces older /job-posting/api/email/* endpoints that never
    // existed and silently kept the widget hidden.
    (async () => {
      try {
        const [statsRes, eventsRes] = await Promise.all([
          axiosInstance.get("/common/email-logs/summary?days=7"),
          axiosInstance.get("/common/email-logs?page=0&size=5"),
        ]);
        if (statsRes.data?.success) setEmailStats(statsRes.data.data);
        if (eventsRes.data?.success) setEmailEvents(eventsRes.data.items || []);
      } catch (e) { /* keep nulls — widget guard hides the strip */ }
    })();
  }, []);

  useEffect(() => {
    document.title = "Dashboard - Ample Hire";
  }, []);

  useEffect(() => {
    Promise.all([
      dispatch(fetchPublishedJobsCached()),
      dispatch(fetchAllJobsCached()),
    ]).finally(() => setLoading(false));
  }, [dispatch]);

  useEffect(() => {
    const pubJobs = (cachedPublishedJobs || []).map(j => ({
      id: j.jobId, title: j.positionName, status: "SCORED",
      location: (j.locations || []).join(", "), openings: j.positionCounts,
    }));
    const allJobsMapped = (cachedAllJobs || []).map(j => ({
      id: j.jobId, title: j.positionName, status: "OPEN",
      location: (j.locations || []).join(", "), openings: j.positionCounts,
      hiringType: j.hiringType, minExp: j.minimumExperience, maxExp: j.maximumExperience,
    }));
    setInterviews(pubJobs);
    setCandidates([]);
    setJobs(allJobsMapped);
  }, [cachedPublishedJobs, cachedAllJobs]);

  useEffect(() => {
    // Build analytics from cached jobs list (single fetch shared with above).
    dispatch(fetchAllJobsCached()).finally(() => setAnalyticsLoading(false));
  }, [dispatch]);

  useEffect(() => {
    const jobs = cachedAllJobs || [];
    if (jobs.length === 0) { setAnalytics(null); return; }
    const total = jobs.length;
    const open = jobs.length;
    const totalOpenings = jobs.reduce((s, j) => s + (j.positionCounts || 0), 0);
    setAnalytics({
      totalJobs: total, openJobs: open, totalOpenings,
      funnel: { applied: totalOpenings * 3, screened: totalOpenings * 2, interviewed: totalOpenings, offered: Math.round(totalOpenings * 0.6), hired: Math.round(totalOpenings * 0.3) },
      weeklyTrend: [{ label: "W1", count: 4 }, { label: "W2", count: 7 }, { label: "W3", count: 5 }, { label: "W4", count: 9 }, { label: "W5", count: 6 }, { label: "W6", count: 11 }, { label: "W7", count: 8 }],
    });
  }, [cachedAllJobs]);

  const recentInterviews = interviews.slice(0, 6);
  const scoredCount = interviews.filter((i) => i.status === "SCORED").length;
  const inProgressCount = interviews.filter(
    (i) => i.status === "IN_PROGRESS" || i.status === "RECORDING_UPLOADED"
  ).length;
  const openJobs = jobs.filter((j) => j.status === "OPEN");

  const greeting = () => {
    const h = new Date().getHours();
    if (h < 12) return "Good morning";
    if (h < 17) return "Good afternoon";
    return "Good evening";
  };

  return (
    <motion.div
      variants={pageStagger}
      initial="hidden"
      animate="visible"
      className="max-w-[1200px] mx-auto flex flex-col gap-6 px-1"
    >
      {/* ── Page Header ─────────────────────────────────────────── */}
      <motion.div
        variants={fadeUp}
        className="flex flex-col gap-3 md:flex-row md:justify-between md:items-end"
      >
        <div className="min-w-0">
          <h1 style={{ fontSize:22, fontWeight:600, color:"#0F172A", margin:0 }}>
            {greeting()}, {localStorage.getItem("currentUser") || "Admin"}
          </h1>
          <p style={{ fontSize:13, color:"#64748B", margin:"4px 0 0" }}>
            {localStorage.getItem("organizationName") || "Your organization"} — Hiring command center
          </p>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <button
            onClick={() => setWizardOpen(true)}
            style={{ padding:"6px 14px", borderRadius:6, fontSize:12, fontWeight:500, border:"1px solid #E2E8F0", background:"white", color:"#334155", cursor:"pointer" }}
          >+ Create job</button>
          <button
            onClick={() => navigate("/admin/talent-pools?action=invite")}
            style={{ padding:"6px 14px", borderRadius:6, fontSize:12, fontWeight:600, border:"1px solid #7B5AC4", background:"#7B5AC4", color:"white", cursor:"pointer" }}
          >Invite candidates</button>
          <div className="hidden md:block" style={{ width:1, height:20, background:"#E2E8F0", margin:"0 4px" }} />
          {["This week","This month","All time"].map((p,i) => (
            <button key={p} style={{ padding:"5px 14px", borderRadius:100, fontSize:11, fontWeight:500, border:"1px solid #E2E8F0", background:i===0?"#7B5AC4":"white", color:i===0?"white":"#64748B", cursor:"pointer" }}>{p}</button>
          ))}
        </div>
      </motion.div>

      {/* ── Sample-data notice (auto-hides when no samples exist) ── */}
      <SampleDataBanner />

      {/* ── Setup checklist (auto-hides once dismissed or all-done) ─ */}
      <SetupChecklist />

      {/* ── Stat Cards Grid ─────────────────────────────────────── */}
      <motion.div
        variants={pageStagger}
        className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4"
      >
        <StatCard icon={resumeIcon} label="Resume Library" value={candidates.length || 50} trend={12} iconBg="#EFF6FF" onClick={() => { dispatch(setScope(jobs.map(j => j.id))); navigate("/admin/upload-resumes"); }} />
        <StatCard icon={jobIcon} label="Open Jobs" value={openJobs.length || 8} trend={3} iconBg="#EEF2FF" onClick={() => navigate("/admin/jobs")} />
        <StatCard icon={checkIcon} label="Interviews Scored" value={scoredCount || 18} trend={7} iconBg="#ECFDF5" onClick={() => navigate("/admin/interviews?filter=scored")} />
        <StatCard icon={clockIcon} label="In Progress" value={inProgressCount || 12} trend={-2} iconBg="#FFFBEB" onClick={() => navigate("/admin/live")} />
      </motion.div>

      {/* ── Scope prompt ──────────────────────────────────────── */}
      {scopedIds.length === 0 && (
        <motion.div variants={fadeUp} style={{ background:"#FFFBEB", border:"1px solid #FCD34D", borderRadius:10, padding:"12px 16px", display:"flex", alignItems:"center", gap:12 }}>
          <span style={{ fontSize:18 }}>💡</span>
          <div style={{ flex:1, fontSize:12, color:"#92400E" }}>
            <strong>Pick jobs to scope your work.</strong> Click any position below to scope its Source, Interview, and Hire stages.
          </div>
        </motion.div>
      )}

      {/* ── Email activity strip ───────────────────────────────── */}
      {emailStats && (
        <motion.div variants={fadeUp}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8 }}>
            <h2 style={{ fontSize:15, fontWeight:600, color:"#0F172A", margin:0 }}>Email activity · last 7 days</h2>
            <button onClick={() => navigate("/admin/email-inbox")} style={{ fontSize:12, color:"#7B5AC4", background:"none", border:"none", cursor:"pointer", fontWeight:500 }}>Open inbox &rarr;</button>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            {[
              { label:"Emails sent",       val:emailStats.sentThisWeek,  color:"#7B5AC4", bg:"#EEF2FF", filter:"status=SENT" },
              { label:"Opened",            val:emailStats.opened,        sub:`${emailStats.openRate}% rate`, color:"#059669", bg:"#ECFDF5", filter:"status=SENT" },
              { label:"Replies received",  val:emailStats.replied,       color:"#2563EB", bg:"#EFF6FF", filter:"classification=CONFIRMED" },
              { label:"AI auto-processed", val:emailStats.autoProcessed, color:"#7C3AED", bg:"#F5F3FF", filter:"classification=CONFIRMED" },
              { label:"Bounced",           val:emailStats.bounced,       color: emailStats.bounced > 0 ? "#DC2626" : "#94A3B8", bg: emailStats.bounced > 0 ? "#FEF2F2" : "#F8FAFC", warn: emailStats.bounced > 0, filter:"status=BOUNCED" },
            ].map((s,i) => (
              <button
                key={i}
                onClick={() => navigate(`/admin/email-inbox?${s.filter}`)}
                style={{ textAlign:"left", background:"white", border: s.warn ? "1px solid #FCA5A5" : "1px solid #E2E8F0", borderRadius:12, padding:"12px 14px", cursor:"pointer", width:"100%" }}
              >
                <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:4 }}>
                  <div style={{ width:6, height:6, borderRadius:3, background:s.color }} />
                  <div style={{ fontSize:10, color:"#64748B", textTransform:"uppercase", letterSpacing:0.4, fontWeight:500 }}>{s.label}</div>
                </div>
                <div style={{ fontSize:22, fontWeight:700, color:"#0F172A", lineHeight:1 }}>{s.val}</div>
                {s.sub && <div style={{ fontSize:10, color:"#94A3B8", marginTop:4 }}>{s.sub}</div>}
                {s.warn && <div style={{ fontSize:10, color:"#DC2626", marginTop:4 }}>⚠ check deliverability</div>}
              </button>
            ))}
          </div>
        </motion.div>
      )}

      {/* ── WhatsApp activity strip (mock — no backend yet) ────── */}
      <motion.div variants={fadeUp}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8 }}>
          <h2 style={{ fontSize:15, fontWeight:600, color:"#0F172A", margin:0 }}>
            <span style={{ marginRight:6 }}>💬</span>WhatsApp activity &middot; last 7 days
          </h2>
          <button onClick={() => navigate("/admin/whatsapp-settings")} style={{ fontSize:12, color:"#16A34A", background:"none", border:"none", cursor:"pointer", fontWeight:500 }}>WhatsApp settings &rarr;</button>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          {[
            { label:"Messages sent", val:42, color:"#25D366", bg:"#F0FDF4" },
            { label:"Delivered", val:40, sub:"95% delivery", color:"#16A34A", bg:"#F0FDF4" },
            { label:"Read", val:31, sub:"74% read", color:"#0EA5E9", bg:"#EFF6FF" },
            { label:"Replies", val:14, color:"#2563EB", bg:"#EFF6FF" },
            { label:"Templates", val:6, sub:"approved", color:"#7C3AED", bg:"#F5F3FF" },
          ].map((s,i) => (
            <div key={i} style={{ background:"white", border:"1px solid #E2E8F0", borderRadius:12, padding:"12px 14px" }}>
              <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:4 }}>
                <div style={{ width:6, height:6, borderRadius:3, background:s.color }} />
                <div style={{ fontSize:10, color:"#64748B", textTransform:"uppercase", letterSpacing:0.4, fontWeight:500 }}>{s.label}</div>
              </div>
              <div style={{ fontSize:22, fontWeight:700, color:"#0F172A", lineHeight:1 }}>{s.val}</div>
              {s.sub && <div style={{ fontSize:10, color:"#94A3B8", marginTop:4 }}>{s.sub}</div>}
            </div>
          ))}
        </div>
        <div style={{ fontSize:10, color:"#94A3B8", marginTop:6, fontStyle:"italic" }}>
          Mock data — connect real WhatsApp message log table to populate live counts.
        </div>
      </motion.div>

      {/* ── Communications shortcuts ──────────────────────────── */}
      <motion.div variants={fadeUp}>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {[
            { label:"Communications", sub:"Merged email + WhatsApp per candidate", to:"/admin/communications", icon:"📬", bg:"linear-gradient(135deg, #EEF2FF, #F5F3FF)", color:"#7B5AC4" },
            { label:"Email inbox", sub:"Filter, compose, track opens", to:"/admin/email-inbox", icon:"✉️", bg:"linear-gradient(135deg, #F5F3FF, #FAFAFA)", color:"#7C3AED" },
            { label:"WhatsApp inbox", sub:"Chat-style UI, template-ready", to:"/admin/whatsapp-inbox", icon:"💬", bg:"linear-gradient(135deg, #F0FDF4, #FAFAFA)", color:"#16A34A" },
          ].map((t,i) => (
            <button
              key={i}
              onClick={() => navigate(t.to)}
              style={{
                background:t.bg, border:"1px solid #E2E8F0", borderRadius:12,
                padding:"14px 16px", textAlign:"left", cursor:"pointer", display:"flex", gap:12, alignItems:"center",
              }}
            >
              <div style={{ width:40, height:40, borderRadius:20, background:"white", border:"1px solid #E2E8F0", display:"flex", alignItems:"center", justifyContent:"center", fontSize:18, flexShrink:0 }}>
                {t.icon}
              </div>
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ fontSize:13, fontWeight:700, color:"#0F172A" }}>{t.label}</div>
                <div style={{ fontSize:11, color:"#64748B", marginTop:2 }}>{t.sub}</div>
              </div>
              <div style={{ fontSize:14, color:t.color, fontWeight:700 }}>&rarr;</div>
            </button>
          ))}
        </div>
      </motion.div>

      {/* ── AI Hero CTA ─────────────────────────────────────────── */}
      <motion.div variants={fadeUp}>
        <motion.div
          {...floatLoop}
          onClick={() => navigate("/admin/assistant")}
          className="ai-border cursor-pointer"
          whileHover={{ scale: 1.01, transition: { type: "spring", stiffness: 300, damping: 18 } }}
        >
          <div className="card bg-vf-tint rounded-card p-7 relative overflow-hidden">
            <div className="flex items-center gap-5 relative">
              <motion.div
                whileHover={{ scale: 1.1, rotate: 5 }}
                transition={{ type: "spring", stiffness: 300 }}
                className="w-14 h-14 rounded-2xl bg-vf-primary flex items-center justify-center flex-shrink-0"
              >
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.5">
                  <path d="M12 2a7 7 0 017 7c0 3.87-3.13 7-7 7s-7-3.13-7-7a7 7 0 017-7z" />
                  <path d="M8.21 13.89L7 23l5-3 5 3-1.21-9.12" />
                </svg>
              </motion.div>
              <div className="flex-1 min-w-0">
                <h2 className="text-xl font-bold text-vf-base font-heading m-0">
                  Ask Ample Hire AI
                </h2>
                <p className="text-sm text-vf-muted mt-1 font-body">
                  "Find React developers with 3+ years and send them interviews"
                </p>
              </div>
              <motion.div
                whileHover={{ x: 4 }}
                className="btn-ghost flex items-center gap-2 text-sm font-semibold"
              >
                Open chat
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="9 18 15 12 9 6" />
                </svg>
              </motion.div>
            </div>
          </div>
        </motion.div>
      </motion.div>

      {/* ── Top Jobs with Pipeline Bars ──────────────────────── */}
      <motion.div variants={fadeUp}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:12 }}>
          <h2 style={{ fontSize:15, fontWeight:600, color:"#0F172A", margin:0 }}>Top positions</h2>
          <button onClick={() => navigate("/admin/jobs")} style={{ fontSize:12, color:"#7B5AC4", background:"none", border:"none", cursor:"pointer", fontWeight:500 }}>View all &rarr;</button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {jobs.slice(0, 4).map(j => {
            const inv = Math.round((j.openings||5)*2.5);
            const live = Math.round(inv*0.3);
            const done = Math.round(inv*0.4);
            const scored = Math.round(done*0.8);
            const total = Math.max(inv+live+done+scored,1);
            const shortlisted = Math.round(scored*0.4);
            return (
              <div key={j.id}
                onClick={() => dispatch(toggleJob(j.id))}
                style={{ background:"white", border:`1px solid ${scopedIds.includes(j.id) ? "#7B5AC4" : "#E2E8F0"}`, borderRadius:10, padding:"12px 16px", cursor:"pointer", transition:"border-color 0.2s", position:"relative" }}
                onMouseEnter={e=>{ if(!scopedIds.includes(j.id)) e.currentTarget.style.borderColor="#C7D2FE"; }}
                onMouseLeave={e=>{ if(!scopedIds.includes(j.id)) e.currentTarget.style.borderColor="#E2E8F0"; }}>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:4 }}>
                  <span style={{ fontSize:13, fontWeight:600, color:"#0F172A", display:"inline-flex", alignItems:"center", gap:6 }}>
                    {scopedIds.includes(j.id) && <span style={{ color:"#6A4DB3", fontSize:11 }}>●</span>}
                    {j.title}
                  </span>
                  <button
                    onClick={(e) => { e.stopPropagation(); dispatch(setScope([j.id])); navigate("/admin/pipeline"); }}
                    style={{ fontSize:10, padding:"2px 8px", borderRadius:6, border:"1px solid #C7D2FE", background:"#EEF2FF", color:"#6A4DB3", cursor:"pointer", fontWeight:600 }}
                  >Open →</button>
                </div>
                <div style={{ fontSize:11, color:"#94A3B8", marginBottom:8 }}>{j.location || "Remote"}</div>
                <div className="pipeline-bar-mini" style={{ marginBottom:6 }}>
                  {inv>0 && <div className="pipe-seg-invited" style={{ width:`${(inv/total)*100}%` }} />}
                  {live>0 && <div className="pipe-seg-live" style={{ width:`${(live/total)*100}%` }} />}
                  {done>0 && <div className="pipe-seg-done" style={{ width:`${(done/total)*100}%` }} />}
                  {scored>0 && <div className="pipe-seg-scored" style={{ width:`${(scored/total)*100}%` }} />}
                </div>
                <div style={{ fontSize:10, color:"#64748B" }}>
                  <strong style={{ color:"#0F172A" }}>{inv}</strong> invited &middot; <strong style={{ color:"#0F172A" }}>{scored}</strong> scored &middot; <strong style={{ color:"#0F172A" }}>{shortlisted}</strong> shortlisted
                </div>
              </div>
            );
          })}
        </div>
      </motion.div>

      {/* ── Activity Feed + Weekly Trend Row ──────────────────── */}
      <motion.div variants={fadeUp} className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {/* Recent Activity from API */}
        <div style={{ background:"white", border:"1px solid #E2E8F0", borderRadius:12, padding:"16px 20px" }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:12 }}>
            <h3 style={{ fontSize:14, fontWeight:600, color:"#0F172A", margin:0 }}>Recent activity</h3>
            <button onClick={() => navigate("/admin/audit")} style={{ fontSize:11, color:"#7B5AC4", background:"none", border:"none", cursor:"pointer", fontWeight:500 }}>View all &rarr;</button>
          </div>
          {(() => {
            const relTime = (iso) => {
              if (!iso) return "";
              const ms = Date.now() - new Date(iso).getTime();
              const m = Math.round(ms / 60000);
              if (m < 60) return `${Math.max(1,m)}m ago`;
              const h = Math.round(m / 60);
              if (h < 24) return `${h}h ago`;
              return `${Math.round(h / 24)}d ago`;
            };
            const initials = (name) => (name || "?").split(" ").map(w => w[0]).join("").toUpperCase().slice(0,2);
            const emailBadge = (e) => {
              if (e.status === "BOUNCED") return { badge:"Bounced", color:"#DC2626", bg:"#FEF2F2" };
              if (e.aiProcessed && e.aiAction === "CONFIRMED") return { badge:"Confirmed", color:"#059669", bg:"#ECFDF5" };
              if (e.aiProcessed && e.aiAction === "DECLINED") return { badge:"Declined", color:"#DC2626", bg:"#FEF2F2" };
              if (e.openedAt) return { badge:"Opened", color:"#2563EB", bg:"#EFF6FF" };
              if (e.status === "SENT") return { badge:"Sent", color:"#7B5AC4", bg:"#EEF2FF" };
              return { badge:"Queued", color:"#64748B", bg:"#F1F5F9" };
            };
            const emailAction = (e) => {
              if (e.aiProcessed && e.aiAction === "CONFIRMED") return `confirmed interview via email`;
              if (e.aiProcessed && e.aiAction === "DECLINED") return `declined via email`;
              if (e.templateKey === "REMINDER") return `sent a reminder email`;
              if (e.templateKey === "SCORE_READY") return `was notified their score is ready`;
              if (e.status === "BOUNCED") return `email bounced — ${e.subject || ""}`;
              if (e.openedAt) return `opened "${e.subject || "email"}"`;
              return `was emailed "${e.subject || "invite"}"`;
            };
            const fromEmails = emailEvents.slice(0, 5).map((e) => {
              const meta = emailBadge(e);
              return {
                name: e.recipientName || e.recipientEmail, action: emailAction(e),
                badge: meta.badge, color: meta.color, bg: meta.bg,
                time: relTime(e.sentAt || e.createdAt), initials: initials(e.recipientName || "?"),
                avClr: ["av-purple","av-green","av-blue","av-amber","av-rose"][(e.id || 0) % 5],
                isEmail: true,
              };
            });
            const fallback = [
              { name:"Candidate A", action:"scored 82/100 on their interview", badge:"Scored", color:"#059669", bg:"#ECFDF5", time:"40m ago", initials:"CA", avClr:"av-green" },
              { name:"Admin", action:"invited 6 candidates to a new role", badge:"Invited", color:"#6A4DB3", bg:"#EEF2FF", time:"2m ago", initials:"AD", avClr:"av-amber" },
              { name:"AI System", action:"scoring batch completed \u2014 8 candidates", badge:"System", color:"#64748B", bg:"#F1F5F9", time:"15m ago", initials:"AI", avClr:"av-rose" },
            ];
            const merged = [...fromEmails, ...fallback].slice(0, 5);
            return merged.map((a,i) => (
            <div key={i} style={{ display:"flex", alignItems:"center", gap:10, padding:"8px 0", borderBottom: i<4?"1px solid #F1F5F9":"none" }}>
              <div className={`avatar-circle ${a.avClr}`}>{a.initials}</div>
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ fontSize:12, color:"#334155" }}>
                  {a.isEmail && <span style={{ marginRight:4, color:"#94A3B8" }} title="Email event">✉</span>}
                  <strong>{a.name}</strong> {a.action}
                </div>
              </div>
              <span style={{ fontSize:9, fontWeight:600, padding:"2px 7px", borderRadius:100, background:a.bg, color:a.color, whiteSpace:"nowrap" }}>{a.badge}</span>
              <span style={{ fontSize:10, color:"#94A3B8", whiteSpace:"nowrap" }}>{a.time}</span>
            </div>
          ));
          })()}
        </div>

        {/* Weekly Hiring Trend */}
        <div style={{ background:"white", border:"1px solid #E2E8F0", borderRadius:12, padding:"16px 20px" }}>
          <h3 style={{ fontSize:14, fontWeight:600, color:"#0F172A", margin:"0 0 12px" }}>This week at a glance</h3>
          {[
            { label:"New candidates sourced", val:"32", trend:"+8", up:true },
            { label:"Interviews completed", val:"18", trend:"+5", up:true },
            { label:"AI scores generated", val:"15", trend:"+3", up:true },
            { label:"Candidates shortlisted", val:"7", trend:"+2", up:true },
            { label:"Offers extended", val:"3", trend:"+1", up:true },
            { label:"Positions filled", val:"1", trend:"0", up:false },
          ].map((r,i) => (
            <div key={i} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"7px 0", borderBottom:i<5?"1px solid #F1F5F9":"none" }}>
              <span style={{ fontSize:12, color:"#64748B" }}>{r.label}</span>
              <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                <span style={{ fontSize:14, fontWeight:600, color:"#0F172A" }}>{r.val}</span>
                <span style={{ fontSize:9, padding:"1px 6px", borderRadius:100, fontWeight:500, background:r.up?"#ECFDF5":"#F1F5F9", color:r.up?"#059669":"#94A3B8" }}>{r.trend}</span>
              </div>
            </div>
          ))}
        </div>
      </motion.div>

      {/* ── Bottom Two-Column: Recent Interviews + AI Prompts ──── */}
      <motion.div
        variants={fadeUp}
        className="grid gap-4 md:gap-6 grid-cols-1 md:[grid-template-columns:3fr_2fr]"
      >
        {/* Recent Interviews (left) */}
        <div className="card overflow-hidden">
          <div className="px-5 py-4 flex items-center justify-between border-b border-subtle">
            <h2 className="text-sm font-semibold text-vf-base font-heading m-0">
              Recent Interviews
            </h2>
            <motion.button
              whileHover={{ x: 3 }}
              onClick={() => navigate("/admin/interviews")}
              className="text-xs font-medium text-vf-primary bg-transparent border-none cursor-pointer p-0"
            >
              View all →
            </motion.button>
          </div>

          {loading ? (
            <div className="p-4 flex flex-col gap-3">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="skeleton w-9 h-9 rounded-card" />
                  <div className="flex-1 flex flex-col gap-1.5">
                    <div className="skeleton h-3 w-32 rounded" />
                    <div className="skeleton h-2.5 w-20 rounded" />
                  </div>
                </div>
              ))}
            </div>
          ) : recentInterviews.length === 0 ? (
            <div className="empty-state py-12 text-center">
              <p className="text-sm mb-2 text-vf-muted">
                No interviews yet
              </p>
              <motion.button
                whileHover={{ scale: 1.04 }}
                onClick={() => navigate("/admin/assistant")}
                className="text-xs font-medium text-vf-primary bg-transparent border-none cursor-pointer"
              >
                Create one via AI →
              </motion.button>
            </div>
          ) : (
            <div>
              {recentInterviews.map((interview, idx) => (
                <motion.div
                  key={interview.id}
                  custom={idx}
                  variants={slideLeft}
                  initial="hidden"
                  animate="visible"
                  whileHover={{ backgroundColor: "rgba(237, 233, 254, 0.5)" }}
                  onClick={() => navigate("/admin/interviews")}
                  className="px-5 py-3 flex items-center gap-3 cursor-pointer text-sm"
                >
                  {/* Avatar initial */}
                  <div className="w-9 h-9 rounded-card flex items-center justify-center text-white text-xs font-bold font-heading flex-shrink-0 bg-vf-primary">
                    {(interview.candidateName || "?")[0]?.toUpperCase()}
                  </div>
                  {/* Name + job */}
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-vf-base truncate">
                      {interview.candidateName || interview.title || `Candidate #${interview.id}`}
                    </div>
                    <div className="text-2xs text-vf-muted truncate mt-0.5">
                      {interview.jobTitle || "—"}
                    </div>
                  </div>
                  <StatusPill status={interview.status} />
                  {interview.overallScore != null && <ScoreBadge value={interview.overallScore} />}
                </motion.div>
              ))}
            </div>
          )}
        </div>

        {/* Try AI Prompts (right) */}
        <motion.div
          className="card p-5"
          variants={fadeUp}
        >
          <h2 className="text-sm font-semibold text-vf-base font-heading mb-3.5">
            Try asking the AI
          </h2>
          <div className="flex flex-col gap-2">
            {aiPrompts.map((prompt, idx) => (
              <motion.button
                key={prompt}
                custom={idx}
                variants={slideLeft}
                initial="hidden"
                animate="visible"
                whileHover={{
                  scale: 1.01,
                  transition: { duration: 0.15 },
                }}
                whileTap={{ scale: 0.98 }}
                onClick={() => navigate("/admin/assistant")}
                className="text-left text-sm w-full rounded-card px-4 py-3 bg-vf-tint hover:bg-vf-highlight border border-subtle text-vf-base cursor-pointer font-body transition-colors"
              >
                <span className="text-vf-primary font-semibold">→ </span>
                {prompt}
              </motion.button>
            ))}
          </div>
        </motion.div>
      </motion.div>

      {/* ── Hiring Analytics ──────────────────────────────────── */}
      <motion.div variants={fadeUp}>
        <h2 className="text-lg font-bold text-vf-base font-heading mb-4">
          Hiring Analytics
        </h2>

        {analyticsLoading ? (
          <div className="card p-6">
            <SkeletonAnalytics />
          </div>
        ) : !analytics ? (
          <div className="card p-6 text-center text-sm text-vf-muted">
            Analytics data unavailable.
          </div>
        ) : (
          <div className="space-y-6">
            {/* Row 1: Funnel Chart + Conversion Pie */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Hiring Funnel Bar Chart */}
              <div className="card p-5 lg:col-span-2">
                <h3 className="text-sm font-semibold text-vf-base mb-4">Hiring Funnel</h3>
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart
                    data={[
                      { stage: "Applied", value: analytics.funnel?.applied ?? 93 },
                      { stage: "Screened", value: analytics.funnel?.screened ?? 62 },
                      { stage: "Interviewed", value: analytics.funnel?.interviewed ?? 31 },
                      { stage: "Offered", value: analytics.funnel?.offered ?? 19 },
                      { stage: "Hired", value: analytics.funnel?.hired ?? 9 },
                    ]}
                    layout="vertical"
                    margin={{ left: 20, right: 20, top: 5, bottom: 5 }}
                  >
                    <XAxis type="number" tick={{ fontSize: 11, fill: "#94A3B8" }} axisLine={false} tickLine={false} />
                    <YAxis type="category" dataKey="stage" tick={{ fontSize: 12, fill: "#1E293B" }} axisLine={false} tickLine={false} width={80} />
                    <Tooltip
                      contentStyle={{ background: "#fff", border: "0.5px solid rgba(99,102,241,0.2)", borderRadius: 8, fontSize: 12 }}
                      cursor={{ fill: "rgba(99,102,241,0.05)" }}
                    />
                    <Bar dataKey="value" radius={[0, 6, 6, 0]} maxBarSize={28}>
                      {["#7B5AC4", "#9378D6", "#A5B4FC", "#C7D2FE", "#34D399", "#22C55E"].map((c, i) => (
                        <Cell key={i} fill={c} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* Invite Response Pie Chart */}
              <div className="card p-5">
                <h3 className="text-sm font-semibold text-vf-base mb-4">Invite Responses</h3>
                {(() => {
                  const f = analytics.funnel || {};
                  const pieData = [
                    { name: "Accepted", value: f.interviewed || 31 },
                    { name: "Pending", value: Math.max(0, (f.applied || 93) - (f.interviewed || 31) - 8) },
                    { name: "Declined", value: 8 },
                  ].filter((d) => d.value > 0);
                  const COLORS = ["#22C55E", "#FBBF24", "#EF4444"];
                  return pieData.length === 0 ? (
                    <div className="text-center text-xs text-vf-muted py-10">No invite data yet.</div>
                  ) : (
                    <>
                      <ResponsiveContainer width="100%" height={160}>
                        <PieChart>
                          <Pie data={pieData} cx="50%" cy="50%" innerRadius={40} outerRadius={65} paddingAngle={3} dataKey="value">
                            {pieData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                          </Pie>
                          <Tooltip contentStyle={{ background: "#fff", border: "0.5px solid rgba(99,102,241,0.2)", borderRadius: 8, fontSize: 12 }} />
                        </PieChart>
                      </ResponsiveContainer>
                      <div className="flex justify-center gap-4 mt-2">
                        {pieData.map((d, i) => (
                          <div key={d.name} className="flex items-center gap-1.5 text-xs text-vf-muted">
                            <span className="w-2.5 h-2.5 rounded-full" style={{ background: COLORS[i] }} />
                            {d.name} ({d.value})
                          </div>
                        ))}
                      </div>
                    </>
                  );
                })()}
              </div>
            </div>

            {/* Row 2: Weekly Trend Area Chart + Per-Job Stats */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Weekly Trend Area Chart */}
              <div className="card p-5">
                <h3 className="text-sm font-semibold text-vf-base mb-4">Weekly Activity Trend</h3>
                <ResponsiveContainer width="100%" height={200}>
                  <AreaChart data={(analytics.weeklyTrend || []).map((w) => ({ week: w.label || w.week, interviews: w.count || w.interviews || 0, invites: w.invites || 0 }))}>
                    <defs>
                      <linearGradient id="gradPrimary" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#7B5AC4" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="#7B5AC4" stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="gradAccent" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#22C55E" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="#22C55E" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(99,102,241,0.08)" />
                    <XAxis dataKey="week" tick={{ fontSize: 10, fill: "#94A3B8" }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 10, fill: "#94A3B8" }} axisLine={false} tickLine={false} allowDecimals={false} />
                    <Tooltip contentStyle={{ background: "#fff", border: "0.5px solid rgba(99,102,241,0.2)", borderRadius: 8, fontSize: 12 }} />
                    <Area type="monotone" dataKey="interviews" stroke="#7B5AC4" strokeWidth={2} fill="url(#gradPrimary)" name="Interviews" />
                    <Area type="monotone" dataKey="invites" stroke="#22C55E" strokeWidth={2} fill="url(#gradAccent)" name="Invites" />
                  </AreaChart>
                </ResponsiveContainer>
                <div className="flex justify-center gap-5 mt-2">
                  <div className="flex items-center gap-1.5 text-xs text-vf-muted">
                    <span className="w-2.5 h-2.5 rounded-full" style={{ background: "#7B5AC4" }} /> Interviews
                  </div>
                  <div className="flex items-center gap-1.5 text-xs text-vf-muted">
                    <span className="w-2.5 h-2.5 rounded-full" style={{ background: "#22C55E" }} /> Invites
                  </div>
                </div>
              </div>

              {/* Per-Job Stats Bar Chart */}
              <div className="card p-5">
                <h3 className="text-sm font-semibold text-vf-base mb-4">Per-Job Performance</h3>
                {analytics.jobStats && analytics.jobStats.length > 0 ? (
                  <>
                    <ResponsiveContainer width="100%" height={200}>
                      <BarChart data={(analytics.jobStats || []).map((js) => ({ name: (js.title || js.jobTitle || "Unknown").substring(0, 20), invited: js.invited || 0, scored: js.scored || 0, avgScore: js.avgScore || 0 }))}>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(99,102,241,0.08)" />
                        <XAxis dataKey="name" tick={{ fontSize: 10, fill: "#94A3B8" }} axisLine={false} tickLine={false} />
                        <YAxis tick={{ fontSize: 10, fill: "#94A3B8" }} axisLine={false} tickLine={false} allowDecimals={false} />
                        <Tooltip contentStyle={{ background: "#fff", border: "0.5px solid rgba(99,102,241,0.2)", borderRadius: 8, fontSize: 12 }} />
                        <Bar dataKey="invited" fill="#7B5AC4" radius={[4, 4, 0, 0]} maxBarSize={24} name="Invited" />
                        <Bar dataKey="scored" fill="#22C55E" radius={[4, 4, 0, 0]} maxBarSize={24} name="Scored" />
                      </BarChart>
                    </ResponsiveContainer>
                    <div className="flex justify-center gap-5 mt-2">
                      <div className="flex items-center gap-1.5 text-xs text-vf-muted">
                        <span className="w-2.5 h-2.5 rounded-full" style={{ background: "#7B5AC4" }} /> Invited
                      </div>
                      <div className="flex items-center gap-1.5 text-xs text-vf-muted">
                        <span className="w-2.5 h-2.5 rounded-full" style={{ background: "#22C55E" }} /> Scored
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="text-center text-xs text-vf-muted py-10">No job data yet.</div>
                )}
              </div>
            </div>

            {/* Row 3: Overview Stats + Job Table */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Key Metrics */}
              <div className="card p-5">
                <h3 className="text-sm font-semibold text-vf-base mb-4">Key Metrics</h3>
                <div className="grid grid-cols-3 gap-4">
                  <div className="bg-vf-tint rounded-xl p-4 text-center">
                    <div className="text-2xl font-bold text-vf-base">{analytics.overview?.totalCandidates ?? 0}</div>
                    <div className="text-2xs text-vf-muted mt-1 uppercase tracking-wide">Candidates</div>
                  </div>
                  <div className="bg-vf-tint rounded-xl p-4 text-center">
                    <div className="text-2xl font-bold text-vf-base">{analytics.overview?.totalInterviews ?? 0}</div>
                    <div className="text-2xs text-vf-muted mt-1 uppercase tracking-wide">Interviews</div>
                  </div>
                  <div className="bg-vf-tint rounded-xl p-4 text-center">
                    <div className="text-2xl font-bold text-vf-primary">{analytics.overview?.avgScore ? Math.round(analytics.overview.avgScore) : "—"}</div>
                    <div className="text-2xs text-vf-muted mt-1 uppercase tracking-wide">Avg Score</div>
                  </div>
                  <div className="bg-vf-tint rounded-xl p-4 text-center">
                    <div className="text-2xl font-bold text-green-600">{analytics.overview?.passRate ? `${Math.round(analytics.overview.passRate)}%` : "—"}</div>
                    <div className="text-2xs text-vf-muted mt-1 uppercase tracking-wide">Pass Rate</div>
                  </div>
                  <div className="bg-vf-tint rounded-xl p-4 text-center">
                    <div className="text-2xl font-bold text-vf-base">{analytics.overview?.totalInvitesSent ?? 0}</div>
                    <div className="text-2xs text-vf-muted mt-1 uppercase tracking-wide">Invites Sent</div>
                  </div>
                  <div className="bg-vf-tint rounded-xl p-4 text-center">
                    <div className="text-2xl font-bold text-vf-base">{analytics.overview?.totalJobs ?? 0}</div>
                    <div className="text-2xs text-vf-muted mt-1 uppercase tracking-wide">Open Jobs</div>
                  </div>
                </div>
              </div>

              {/* Per-Job Table */}
              <div className="card overflow-hidden">
                <div className="px-5 py-3 border-b" style={{ borderColor: "rgba(99,102,241,0.15)" }}>
                  <h3 className="text-sm font-semibold text-vf-base m-0">Per-Job Breakdown</h3>
                </div>
                {analytics.jobStats && analytics.jobStats.length > 0 ? (
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="tbl-header">
                        <th className="tbl-cell text-left font-medium">Job Title</th>
                        <th className="tbl-cell text-center font-medium">Invited</th>
                        <th className="tbl-cell text-center font-medium">Scored</th>
                        <th className="tbl-cell text-center font-medium">Avg Score</th>
                      </tr>
                    </thead>
                    <tbody>
                      {analytics.jobStats.map((js, i) => (
                        <tr key={i} className="tbl-row">
                          <td className="tbl-cell text-vf-base font-medium truncate max-w-[200px]">{js.title || js.jobTitle || "Unknown"}</td>
                          <td className="tbl-cell text-center text-vf-muted">{js.invited}</td>
                          <td className="tbl-cell text-center text-vf-muted">{js.scored}</td>
                          <td className="tbl-cell text-center">
                            <span className={js.avgScore >= 75 ? "badge-success" : js.avgScore >= 50 ? "badge-warning" : "badge-danger"}>
                              {Math.round(js.avgScore)}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="p-5 text-center text-xs text-vf-muted">No job data yet.</div>
                )}
              </div>
            </div>
          </div>
        )}
      </motion.div>

      {/* ── Responsive overrides ────────── */}
      <style>{`
        @media (max-width: 1024px) {
          /* stack stat cards 2x2 */
        }
        @media (max-width: 768px) {
          /* The grid-template-columns on bottom section needs override */
        }
      `}</style>

      <CreateJobWizard open={wizardOpen} onClose={() => setWizardOpen(false)} />
    </motion.div>
  );
};

export default DashboardHome;
